import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import type { PerfilDaUsuaria } from '@reducee/contracts';

import { chamarApi } from '../api/cliente.ts';
import logoReducee from '../ativos/reducee.png';
import { ROTULO_PAPEL, useSessao } from '../autenticacao/sessao.tsx';
import { Avatar, enderecoDaMinhaFoto } from '../componentes/Avatar.tsx';

type ItemDeMenu = { rotulo: string; para: string; somenteAdministradora?: boolean };

/**
 * Menu agrupado pelo momento da operação, não pelo módulo técnico: primeiro o
 * que traz dinheiro, depois o que sustenta o contrato, depois o que remunera,
 * e por último o que se configura uma vez.
 *
 * Quem não é Administradora vê só Dashboard, Sales, Tarefas e Metas (NEX-222).
 * As rotas em `App.tsx` e a API seguem o mesmo recorte; o menu só o reflete.
 */
const GRUPOS_DE_MENU: Array<{ titulo: string; itens: ItemDeMenu[] }> = [
  {
    titulo: 'Gestão',
    itens: [
      { rotulo: 'Dashboard', para: '/' },
      // Sales reúne funil, fila de atendimento e cadência numa tela só, como no
      // protótipo aprovado. "Atender agora" e "Cadências" continuam existindo
      // como rota, alcançáveis de dentro do Sales, mas não disputam o menu.
      { rotulo: 'Sales', para: '/sales' },
      // Saiu do menu no alinhamento ao protótipo (15/09) e voltou na NEX-231:
      // sem ele, a lista de identidades só se abria pelo link da Visão 360º.
      { rotulo: 'Contatos', para: '/contatos', somenteAdministradora: true },
      { rotulo: 'Clientes 360º', para: '/clientes', somenteAdministradora: true },
      { rotulo: 'Importar Leads', para: '/importar-leads', somenteAdministradora: true },
    ],
  },
  {
    titulo: 'Pós-venda',
    itens: [
      { rotulo: 'Implantação', para: '/implantacao', somenteAdministradora: true },
      { rotulo: 'Movimentações', para: '/movimentacoes', somenteAdministradora: true },
      { rotulo: 'Renovações', para: '/renovacoes', somenteAdministradora: true },
      { rotulo: 'Notificações', para: '/notificacoes', somenteAdministradora: true },
      { rotulo: 'Tarefas', para: '/tarefas' },
    ],
  },
  {
    titulo: 'Financeiro comercial',
    itens: [
      { rotulo: 'Comissões', para: '/comissoes', somenteAdministradora: true },
      { rotulo: 'Metas & Bonificação', para: '/metas' },
    ],
  },
  {
    // Usuárias e Auditoria vivem dentro de Configurações, como no protótipo.
    titulo: 'Administração',
    itens: [{ rotulo: 'Configurações', para: '/configuracoes', somenteAdministradora: true }],
  },
];

type ResultadoDaBusca = { id: string; nome: string; ehCliente: boolean };

/** O `md` do Tailwind, para o JavaScript enxergar o mesmo ponto de quebra que o CSS. */
const CONSULTA_DE_DESKTOP = '(min-width: 48rem)';

/**
 * Casca do sistema: menu lateral sempre aberto no desktop, com o nome de todos
 * os módulos visível — nunca modo só-ícones.
 *
 * Abaixo de `md` o mesmo menu vira gaveta. Não é enfeite: em 390 px os 300 px
 * fixos da barra comiam 77% da largura, e a Comercial trabalha na rua, com SLA
 * de quinze minutos para o primeiro contato com o lead da Meta.
 */
export function Aplicacao(): React.JSX.Element {
  const { usuaria, sair } = useSessao();
  const [gavetaAberta, definirGavetaAberta] = useState(false);

  // Só para a foto do rodapé: nome e papel vêm da sessão, que já está aqui.
  const perfil = useQuery({
    queryKey: ['perfil'],
    queryFn: () => chamarApi<PerfilDaUsuaria>('/perfil'),
    enabled: Boolean(usuaria),
    staleTime: Infinity,
  });

  /**
   * Esc fecha a gaveta, e enquanto ela está aberta o corpo não rola: no celular
   * a gaveta cobre a tela, e rolar o que está atrás dela faz a pessoa voltar
   * para um lugar diferente de onde parou.
   */
  useEffect(() => {
    if (!gavetaAberta) return;

    function aoTeclar(evento: KeyboardEvent): void {
      if (evento.key === 'Escape') definirGavetaAberta(false);
    }

    const rolagemAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', aoTeclar);

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = rolagemAnterior;
    };
  }, [gavetaAberta]);

  /**
   * Girar o aparelho ou crescer a janela até `md` fecha a gaveta. Sem isso, o
   * estado aberto sobrevive à passagem para desktop e deixa a rolagem do corpo
   * travada, sem nada na tela que explique por quê.
   */
  useEffect(() => {
    const desktop = window.matchMedia(CONSULTA_DE_DESKTOP);

    function aoMudarALargura(): void {
      if (desktop.matches) definirGavetaAberta(false);
    }

    desktop.addEventListener('change', aoMudarALargura);
    return () => desktop.removeEventListener('change', aoMudarALargura);
  }, []);

  if (!usuaria) return <></>;

  const ehAdministradora = usuaria.papel === 'ADMINISTRADORA';

  return (
    <div className="flex min-h-full">
      {/* O véu só existe no celular: tocar fora para fechar é o gesto esperado
       * numa gaveta. Fica fora da árvore de acessibilidade de propósito — o ✕ e
       * o Esc já fecham, e um segundo controle chamado "Fechar o menu" só criaria
       * ambiguidade no leitor de tela. O `tabIndex` negativo acompanha porque
       * `aria-hidden` não pode conter coisa focável. */}
      {gavetaAberta ? (
        <button
          type="button"
          data-teste="veu-do-menu"
          aria-hidden="true"
          tabIndex={-1}
          onClick={() => definirGavetaAberta(false)}
          className="fixed inset-0 z-30 bg-marinho/50 md:hidden"
        />
      ) : null}

      <aside
        id="menu-lateral"
        data-teste="menu-lateral"
        className={`fixed inset-y-0 left-0 z-40 flex h-screen w-[300px] shrink-0 flex-col overflow-y-auto overflow-x-hidden border-r border-dourado/20 bg-marinho px-4 pb-6 pt-4.5 text-white shadow-[10px_0_32px_rgba(7,18,63,0.08)] transition-transform duration-200 motion-reduce:transition-none md:sticky md:inset-y-auto md:left-auto md:top-0 md:z-auto md:visible md:translate-x-0 ${
          gavetaAberta ? 'visible translate-x-0' : 'invisible -translate-x-full'
        }`}
      >
        {/* A marca fica presa no topo: rolar o menu longo não deve custar a
         * referência de onde se está. O `pr-14` reserva a faixa do ✕ no
         * celular: sem ele o botão cai sobre o cartão branco do logo, e um ✕
         * branco sobre branco some. */}
        <div
          className="sticky -top-[18px] z-10 -mt-[18px] flex min-h-[104px] items-center justify-center border-b border-white/10 bg-marinho px-2.5 pb-4.5 pr-14 pt-4.5 md:pr-2.5">
          <img
            src={logoReducee}
            alt="Reducee Saúde e Benefícios"
            className="block h-auto max-h-[88px] w-[242px] max-w-full rounded-[14px] border border-dourado/45 bg-white px-3.75 py-3.25 object-contain shadow-[0_10px_26px_rgba(0,0,0,0.18)]"
          />

          {/* Fechar existe só no celular: no desktop a barra não tem estado
           * fechado, e um ✕ ali prometeria uma ação que não acontece. */}
          <button
            type="button"
            data-teste="fechar-menu"
            aria-label="Fechar o menu"
            onClick={() => definirGavetaAberta(false)}
            className="absolute right-0 top-0 flex size-11 items-center justify-center rounded-[10px] text-lg leading-none text-white/80 transition hover:bg-white/10 md:hidden"
          >
            <span aria-hidden>✕</span>
          </button>
        </div>

        <nav className="flex-1">
          {GRUPOS_DE_MENU.map((grupo) => {
            const itens = grupo.itens.filter(
              (item) => !item.somenteAdministradora || ehAdministradora,
            );
            if (itens.length === 0) return null;

            return (
              <div key={grupo.titulo}>
                <p className="mx-3 mb-2 mt-4.75 text-[10px] font-[850] uppercase tracking-[0.14em] text-dourado/90">
                  {grupo.titulo}
                </p>
                <ul>
                  {itens.map((item) => (
                    <li key={item.para}>
                      <NavLink
                        to={item.para}
                        end={item.para === '/'}
                        // Navegar fecha a gaveta: ela cobre a tela inteira no
                        // celular, e deixá-la aberta esconderia justamente a
                        // página que a pessoa acabou de pedir.
                        onClick={() => definirGavetaAberta(false)}
                        className={({ isActive }) =>
                          `my-0.75 flex min-h-[44px] items-center gap-2.75 rounded-[10px] px-3 py-2.75 text-[13px] font-[650] leading-tight transition ${
                            isActive
                              ? 'bg-white/[0.11] text-white shadow-[inset_3px_0_0_var(--color-dourado)]'
                              : 'text-[#eef2ff] hover:bg-white/[0.11] hover:text-white'
                          }`
                        }
                      >
                        <span className="size-2 shrink-0 rounded-full bg-dourado" />
                        <span>{item.rotulo}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* Quem está logada fica no rodapé do menu, como no protótipo: é
         * informação de contexto, não uma ação do cabeçalho. O bloco inteiro
         * leva a Meu perfil, que é onde se troca o que ele mostra. */}
        <NavLink
          to="/perfil"
          data-teste="usuaria-autenticada"
          onClick={() => definirGavetaAberta(false)}
          className={({ isActive }) =>
            `mt-6.5 flex min-h-[44px] items-center gap-2.75 rounded-xl border-t border-white/15 px-2.5 pb-2.5 pt-4 text-xs transition hover:bg-white/[0.11] ${
              isActive ? 'bg-white/[0.11]' : 'bg-white/[0.035]'
            }`
          }
        >
          <Avatar
            nome={usuaria.nome}
            foto={enderecoDaMinhaFoto(perfil.data?.versaoDaFoto ?? null)}
          />
          <span className="min-w-0">
            <strong className="block truncate text-[13px]">{usuaria.nome}</strong>
            <span data-teste="papel-usuaria" className="block text-white/70">
              {ROTULO_PAPEL[usuaria.papel]}
            </span>
          </span>
        </NavLink>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-[72px] shrink-0 items-center justify-between gap-3 border-b border-linha bg-superficie px-4 md:gap-4 md:px-6.5">
          {/* Abaixo de `md` este botão é tudo o que o menu ocupa até ser
           * aberto. Os 44 px são o alvo de toque mínimo. */}
          <button
            type="button"
            data-teste="abrir-menu"
            aria-label="Abrir o menu"
            aria-expanded={gavetaAberta}
            aria-controls="menu-lateral"
            onClick={() => definirGavetaAberta((aberta) => !aberta)}
            className="flex size-11 shrink-0 items-center justify-center rounded-[10px] border border-linha text-xl leading-none transition hover:bg-marinho-suave md:hidden"
          >
            <span aria-hidden>☰</span>
          </button>

          <BuscaGlobal />

          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={() => void sair()}
              className="min-h-[44px] rounded-[10px] border border-linha px-3.25 py-2.5 text-base font-[750] transition hover:bg-marinho-suave"
            >
              Sair
            </button>
          </div>
        </header>

        {/* O respiro acompanha o cabeçalho: 26px de cada lado eram 13% de
         * um celular, e deixá-los aqui enquanto o cabeçalho encolheu para 16
         * desalinharia a coluna de conteúdo do que está acima dela. */}
        <main className="min-w-0 flex-1 p-4 md:p-6.5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Busca de Contato no cabeçalho.
 *
 * Varre os Contatos inteiros, e não só a carteira. A Visão 360º de quem ainda
 * não fechou venda não tinha porta nenhuma na interface: a tela Clientes 360º
 * lista só quem tem Contrato — "Cliente é o Contato com Contrato" — e a página
 * que listava todos saiu do menu. Quem quisesse olhar a jornada de um lead,
 * com origem, consentimento, interações e cadência, só chegava lá digitando o
 * endereço.
 *
 * É também o que o protótipo desenha: o campo dele diz "Buscar cliente,
 * contrato ou oportunidade", e não "na carteira" — foi a aplicação que
 * estreitou o campo depois.
 *
 * O recorte por papel continua valendo porque vem da API: a Comercial recebe
 * somente os Contatos que conduz.
 */
function BuscaGlobal(): React.JSX.Element {
  const [termo, definirTermo] = useState('');
  const [aberta, definirAberta] = useState(false);
  const navegar = useNavigate();
  const container = useRef<HTMLDivElement>(null);

  const consulta = useQuery({
    queryKey: ['contatos'],
    queryFn: () => chamarApi<ResultadoDaBusca[]>('/contatos'),
    enabled: termo.trim().length >= 2,
  });

  useEffect(() => {
    function aoClicarFora(evento: MouseEvent): void {
      if (!container.current?.contains(evento.target as Node)) definirAberta(false);
    }
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, []);

  /**
   * O filtro é local e a consulta fica guardada sob a chave dos Contatos: a
   * lista inteira já é lida por outras telas, então digitar não dispara uma
   * busca por tecla nem espera a rede a cada letra.
   */
  const procurado = termo.trim().toLowerCase();
  const resultados = (consulta.data ?? [])
    .filter((contato) => contato.nome.toLowerCase().includes(procurado))
    .slice(0, 8);

  return (
    // No celular a busca ocupa o que sobra entre o botão do menu e o Sair; do
    // `md` para cima ela volta à largura fixa do protótipo.
    <div ref={container} className="relative w-full min-w-0 md:w-[min(520px,55vw)]">
      <div className="flex items-center gap-2.25 rounded-xl border border-linha bg-campo px-3 py-2.25 focus-within:border-marinho-claro">
        <span aria-hidden className="text-texto-suave">
          ⌕
        </span>
        <input
          type="search"
          value={termo}
          data-teste="busca-global"
          onChange={(evento) => {
            definirTermo(evento.target.value);
            definirAberta(true);
          }}
          onFocus={() => definirAberta(true)}
          placeholder="Buscar cliente ou lead…"
          className="w-full border-0 bg-transparent text-sm outline-none"
        />
      </div>

      {aberta && termo.trim().length >= 2 ? (
        <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg bg-superficie shadow-lg ring-1 ring-linha">
          {resultados.length === 0 ? (
            <li className="px-3 py-2 text-sm text-texto-suave">
              {consulta.isLoading ? 'Buscando…' : 'Nenhum contato encontrado.'}
            </li>
          ) : (
            resultados.map((resultado) => (
              <li key={resultado.id}>
                <button
                  type="button"
                  data-teste="resultado-da-busca"
                  onClick={() => {
                    definirAberta(false);
                    definirTermo('');
                    void navegar(`/contatos/${resultado.id}`);
                  }}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-marinho-suave"
                >
                  <span className="font-medium">{resultado.nome}</span>
                  {/* Dizer o que se está abrindo: a mesma palavra que a ficha usa. */}
                  <span className="ml-2 text-xs text-texto-suave">
                    {resultado.ehCliente ? 'Cliente' : 'Ainda não é Cliente'}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
